import { IndustryProofsLight } from '@/app/components/quizlet/components/industry-proofs/industry-proofs-light';
import { AnswerModel } from '@/components/decision-tree/hooks';
import { HeroVideo } from '@/components/hero-video/hero-video';
import customTheme from '@/constants/theme.custom';
import { TOAST_DEFAULT_OPTIONS } from '@/constants/toast';
import { useEnterKeyGlobal } from '@/hooks/use-enter-key';
import { Box, Button, Flex, Image, Section, Text, useToast } from '@cmpsr/components';
import { useState, useEffect } from 'react';
import { useInfoImageInterstitialController } from '../InfoImageInterstitial/InfoImageInterstitial.controller';

export type HeroVideoInterstitialType = {
  id: string;
  type: 'heroVideoInterstitial';
  surveyItemType: 'interstitial';
  title: string;
  subtitle: string;
  text: string;
  resource: string;
};

export type HeroVideoInterstitialProps = {
  data: HeroVideoInterstitialType;
  saveAnswer: (payload: AnswerModel) => void;
  goToNextSurveyItem: () => void;
};

export const HeroVideoInterstitial = ({ data, saveAnswer, goToNextSurveyItem }: HeroVideoInterstitialProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const toast = useToast();

  const { handleNextClick: controllerHandleNext } = useInfoImageInterstitialController({
    saveAnswer,
    data: {
      ...data,
      type: 'infoImageInterstitial' as const,
    },
    goToNextSurveyItem,
  });

  // Handle next button click
  const handleNextClick = async () => {
    setIsLoading(true);
    try {
      await controllerHandleNext();
    } catch (error) {
      toast({
        ...TOAST_DEFAULT_OPTIONS,
        title: 'Error',
        description: error,
        status: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEnterKeyGlobal(handleNextClick);

  // Prevent body scroll when component is mounted
  useEffect(() => {
    const originalStyle = document.body.style.cssText;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    
    return () => {
      document.body.style.cssText = originalStyle;
    };
  }, []);

  return (
    <Box 
      width="100%" 
      height="100%" 
      minHeight="100svh"
      background="white" 
      display="flex" 
      flexDirection="column" 
      overflow="hidden"
      position="fixed"
      inset={0}
    >
      <Box display="flex" flexDirection="column" height="100%">
        <Box flex="1 1 auto" minHeight={0} overflow="hidden" position="relative">
          <HeroVideo src="/videos/catch-hero.mp4">
            <Flex flexDirection="column" alignItems="center" justifyContent="center" gap="8px" pb="5rem">
              <Image
                src="/images/risk-reduction-animation.gif"
                alt="Risk Reduction Animation"
                width={{ base: '90%', lg: '400px' }}
                maxHeight="50%"
              />
              <Text
                color="white"
                fontSize={{ base: '1.5625rem', lg: '4.54688rem' }}
                fontWeight="400"
                lineHeight={{ base: '130%', lg: '111%' }}
                maxWidth="58rem"
              >
                Learn Your Cancer Risk For 21 Cancers with Catch
              </Text>
            </Flex>
          </HeroVideo>
        </Box>
        {/* Modal */}
        <Box
          width="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          pt="spacer-8"
          pb={{ base: 'spacer-8', lg: 'spacer-16' }}
          mt="-48px"
          borderRadius="2rem"
          bgGradient="radial(circle, white, #F5F5F7)"
          zIndex="1"
          flex="0 0 auto"
          overflowY="auto"
          overflowX="hidden"
          maxHeight="100%"
        >
          <Box display="flex" flexDirection="column" gap="spacer-8" width="100%">
            <Flex flexDirection="column" gap="1.5rem">
              <Section position="relative" borderRadius="2rem" overflow="hidden" width="95%" mx="auto" maxW="1200px">
                <Box
                  as="video"
                  src="/videos/cells.mp4"
                  autoPlay
                  loop
                  muted
                  playsInline
                  position="absolute"
                  top="-10%"
                  left="-5%"
                  width="110%"
                  height="120%"
                  objectFit="cover"
                  zIndex={0}
                  style={{
                    minHeight: '120%',
                    minWidth: '110%',
                  }}
                />
                {/* Overlay */}
                <Box position="absolute" top={0} left={0} right={0} bottom={0} bg="rgba(0, 0, 0, 0.7)" zIndex={2} />
                {/* Background pattern overlay */}
                <Box position="absolute" top={0} left={0} right={0} bottom={0} zIndex={1} opacity={0.3}>
                  {/* Radial gradient mask */}
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    right={0}
                    bottom={0}
                    bgGradient="radial-gradient(circle at 1% center, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 95%)"
                  />
                  {/* Grid lines pattern */}
                  <Box position="absolute" top={0} left={0} width="66.67%" bottom={0}>
                    {/* Vertical lines */}
                    <Box position="absolute" top={0} left={0} right={0} bottom={0}>
                      {Array.from({ length: 13 }, (_, i) => (
                        <Box
                          key={`vertical-${i}`}
                          position="absolute"
                          top={0}
                          bottom={0}
                          left={`${40 + i * 80}px`}
                          width="1px"
                          bg={i === 11 || i === 12 ? '#D0D5DD' : 'white'}
                          opacity={i === 0 || i === 12 ? 0 : 1}
                        />
                      ))}
                    </Box>
                    {/* Horizontal lines */}
                    <Box position="absolute" top={0} left={0} right={0} bottom={0}>
                      {Array.from({ length: 13 }, (_, i) => (
                        <Box
                          key={`horizontal-${i}`}
                          position="absolute"
                          left={0}
                          width="100%"
                          top={`${40 + i * 80}px`}
                          height="1px"
                          bg={i === 11 || i === 12 ? '#D0D5DD' : 'white'}
                          opacity={i === 0 || i === 12 ? 0 : 1}
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>
                <Box
                  position="relative"
                  zIndex={2}
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                >
                  <Flex
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    gap={{ base: '1rem', lg: '2rem' }}
                    py={{ base: '2rem', lg: '4rem' }}
                    px={{ base: '1rem', lg: '2rem' }}
                    mx="auto"
                  >
                    <Text color="white" fontSize="1rem" fontWeight={600} lineHeight="130%" textAlign="center">
                      Built on 500+ verified risk factors from over 10,000 clinical studies
                    </Text>
                    <IndustryProofsLight />
                  </Flex>
                </Box>
              </Section>
              <Button
                variant="primary"
                backgroundColor="salmon-light"
                color="darkBlue"
                fontFamily={customTheme.typography.fonts.inter}
                type="submit"
                isLoading={isLoading}
                onClick={handleNextClick}
                px={{ base: 'spacer-5', lg: 'spacer-8' }}
                py={{ base: 'spacer-5', lg: 'spacer-5' }}
                width={{ base: '95%', sm: '95%', md: '608px' }}
                maxWidth={{ base: '1200px', md: '608px' }}
                alignSelf="center"
                _hover={{
                  backgroundColor: 'white-100',
                }}
                _selected={{
                  backgroundColor: 'white-100',
                }}
                _focus={{
                  backgroundColor: 'white-100',
                }}
              >
                Get Started
              </Button>
            </Flex>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
